<template>
  <div class="form-list">
    <el-form
      ref="form"
      :model="details"
      :rules="rules"
      label-position="right"
      label-width="160px"
      class="form"
    >
      <div class="table-title table-title-top">申请信息</div>
      <el-form-item label="申请时间">
        {{ details.add_time | timeToYmdHi }}
      </el-form-item>

      <el-form-item label="申请者">
        <v-avatar-nickname
          v-if="details.administrator && details.administrator.id"
          :value="details.administrator.avatar"
          :nickname="details.administrator.nickname"
          :is-center="false"
        />
        <span v-else class="success-color">学员本人</span>
      </el-form-item>

      <el-form-item label="申请门店">
        {{ details.add_store_name }}
      </el-form-item>

      <el-form-item label="申请操作">
        <strong>{{ findCardUpdateTypeName(details.update_type) }}</strong>
      </el-form-item>

      <el-form-item label="申请原因">
        <span v-if="details.update_remark" class="danger-color">{{ details.update_remark }}</span>
        <span v-else class="info-color">未填写</span>
      </el-form-item>

      <!-- 学员申请停卡：无字段变更，展示“是哪张卡 + 停卡原因/天数/凭证”，供审核者判断 -->
      <template v-if="isStopApply">
        <div class="table-title">课卡信息</div>

        <el-form-item label="课卡 ID">{{ details.card_id }}</el-form-item>

        <el-form-item label="所属学员">
          <v-avatar-nickname
            :value="details.student.selfie"
            :nickname="details.student.student_name"
            :is-center="false"
            :link-page-name="RouteConfig.studentDetail.name"
            :link-page-id="details.student.id"
          />
        </el-form-item>

        <el-form-item label="课卡名称">{{ details.card_name }}</el-form-item>

        <el-form-item label="课卡类型">{{ details.card_type_name }}</el-form-item>

        <el-form-item label="办卡门店">{{ details.store_name }}</el-form-item>

        <el-form-item label="剩余次数">
          <span v-if="details.is_number_limit">{{ details.expire_number | toYuan }} 次</span>
          <span v-else class="info-color">不限次数</span>
        </el-form-item>

        <el-form-item label="过期时间">
          <span v-if="details.expire_time">{{ details.expire_time | timeToYmdHi }}</span>
          <span v-else class="info-color">未开卡</span>
        </el-form-item>

        <div class="table-title">停卡申请信息</div>

        <el-form-item label="停卡原因类型">
          <span v-if="details.stop_reason_type_name">{{ details.stop_reason_type_name }}</span>
          <span v-else class="info-color">未填写</span>
        </el-form-item>

        <el-form-item label="学员申请天数">
          {{ details.stop_days | toYuan }} 天
        </el-form-item>

        <el-form-item v-if="canEditStopDays" label="审核停卡天数">
          <v-input-number
            class="w100"
            v-model="stopDaysInput"
            type="unsignedSmallInt"
            :min="0.5"
            :max="365"
            :precision="2"
            :is-show-suffix="true"
            suffix-name="天"
            :disabled="isExamining"
          />
          <div v-if="isStopDaysInputInvalid" class="danger-color">低于下限，离开输入框后会自动调整为 0.5 天</div>
          <el-alert
            title="默认按学员申请天数执行；如核实需要更长时间，可在此调整为 0.5 ~ 365 天，最长 1 年。"
            type="info"
            show-icon
            :closable="false"
          />
        </el-form-item>
        <el-form-item v-else label="审核停卡天数">
          <span v-if="details.stop_days_approved">{{ details.stop_days_approved | toYuan }} 天（审核通过之日起算，到期自动开卡）</span>
          <span v-else class="info-color">待审核</span>
        </el-form-item>

        <el-form-item label="上传凭证">
          <v-remark-file-list v-if="(details.remark_files || []).length" :files="details.remark_files || []"/>
          <span v-else class="info-color">未上传</span>
        </el-form-item>
      </template>

      <template v-if="!isStopApply">
      <div class="table-title">课卡信息</div>

      <div class="field-group-label">基础信息</div>

      <el-form-item label="所属学员">
        <template v-if="details.old_student.id !== details.student.id">
          <v-avatar-nickname
            v-if="details.old_student.id"
            :value="details.old_student.selfie"
            :nickname="details.old_student.student_name"
            :is-center="false"
            :link-page-name="RouteConfig.studentDetail.name"
            :link-page-id="details.old_student.id"
          />
          <v-icon name="arrow-right"/>
          <v-avatar-nickname
            :value="details.student.selfie"
            :nickname="details.student.student_name"
            :is-center="false"
            :link-page-name="RouteConfig.studentDetail.name"
            :link-page-id="details.student.id"
          />
        </template>
        <v-avatar-nickname
          v-else
          :value="details.old_student.selfie"
          :nickname="details.old_student.student_name"
          :is-center="false"
          :link-page-name="RouteConfig.studentDetail.name"
          :link-page-id="details.old_student.id"
        />
      </el-form-item>

      <el-form-item label="办卡日期">
        <template v-if="details.old_register_time !== details.register_time">
          <span class="user-select">{{ details.old_register_time | timeToYmd }}</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.register_time | timeToYmd }}</span>
        </template>
        <template v-else>{{ details.register_time | timeToYmd }}</template>
      </el-form-item>

      <el-form-item label="自动开卡日期">
        <template v-if="details.old_start_time_auto !== details.start_time_auto">
          <span v-if="details.old_start_time_auto" class="user-select">{{ details.old_start_time_auto | timeToYmdHi }}</span>
          <span v-else class="info-color">未设置</span>
          <v-icon name="arrow-right"/>
          <span v-if="details.start_time_auto" class="primary-color user-select">{{ details.start_time_auto | timeToYmdHi }}</span>
          <span v-else class="info-color">未设置</span>
        </template>
        <template v-else>
          <template v-if="details.start_time_auto">{{ details.start_time_auto | timeToYmdHi }}</template>
          <span v-else class="info-color">未设置</span>
        </template>
      </el-form-item>

      <el-form-item label="办卡门店">
        {{ details.store_name }}
      </el-form-item>

      <el-form-item label="课卡名称">
        <template v-if="details.old_card_name !== details.card_name">
          <span class="user-select">{{ details.old_card_name }}</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.card_name }}</span>
        </template>
        <template v-else>{{ details.card_name }}</template>
      </el-form-item>

      <el-form-item label="禁止预约专栏">
        <template v-if="details.old_is_disable_column !== details.is_disable_column">
          <span class="user-select">{{ details.old_is_disable_column ? '是' : '否' }}</span>
          <v-icon name="arrow-right"/>
          <span :class="details.is_disable_column ? 'danger-color user-select' : 'primary-color user-select'">{{ details.is_disable_column ? '是' : '否' }}</span>
        </template>
        <template v-else>
          <span v-if="details.is_disable_column" class="danger-color">是</span>
          <span v-else>否</span>
        </template>
      </el-form-item>

      <el-form-item label="课卡类型">
        <template v-if="details.old_card_type_name !== details.card_type_name">
          <span class="user-select">{{ details.old_card_type_name }}</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.card_type_name }}</span>
        </template>
        <template v-else>{{ details.card_type_name }}</template>
      </el-form-item>

      <div class="field-group-label">使用规则</div>

      <el-form-item label="适用门店">
        <template v-if="details.old_store_names !== details.store_names">
          <span class="user-select">{{ details.old_store_names || '全国通用' }}</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.store_names || '全国通用' }}</span>
        </template>
        <template v-else>{{ details.store_names || '全国通用' }}</template>
      </el-form-item>

      <template v-if="details.store_names">
        <el-form-item label="适用门店限制天数">
          <template v-if="details.old_store_limit_expire_days !== details.store_limit_expire_days">
            <span class="user-select">{{ details.old_store_limit_expire_days }} 天</span>
            <v-icon name="arrow-right"/>
            <span class="primary-color user-select">{{ details.store_limit_expire_days }} 天</span>
          </template>
          <template v-else>{{ details.store_limit_expire_days }} 天</template>
        </el-form-item>

        <el-form-item label="适用门店失效日期">
          <template v-if="details.old_store_limit_expire_time !== details.store_limit_expire_time">
            <span class="user-select">{{ details.old_store_limit_expire_time | timeToYmdHi }}</span>
            <v-icon name="arrow-right"/>
            <span class="primary-color user-select">{{ details.store_limit_expire_time | timeToYmdHi }}</span>
          </template>
          <template v-else>{{ details.store_limit_expire_time | timeToYmdHi }}</template>
        </el-form-item>
      </template>

      <el-form-item label="次数限定">
        <template v-if="details.old_is_number_limit !== details.is_number_limit">
          <span class="user-select">{{ details.old_is_number_limit ? '是' : '否' }}</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.is_number_limit ? '是' : '否' }}</span>
        </template>
        <template v-else>{{ details.is_number_limit ? '是' : '否' }}</template>
      </el-form-item>

      <el-form-item label="剩余次数">
        <template v-if="details.old_expire_number !== details.expire_number">
          <span class="user-select">{{ details.old_expire_number / 100 }}</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.expire_number | toYuan }}</span>
        </template>
        <template v-else>{{ details.expire_number | toYuan }}</template>
      </el-form-item>

      <el-form-item label="有效天数">
        <template v-if="details.old_expire_days !== details.expire_days">
          <span class="user-select">{{ details.old_expire_days }} 天</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.expire_days }} 天</span>
        </template>
        <template v-else>{{ details.expire_days }} 天</template>
      </el-form-item>

      <el-form-item label="过期时间">
        <template v-if="details.old_expire_time !== details.expire_time">
          <span v-if="details.old_expire_time" class="user-select">{{ details.old_expire_time | timeToYmdHi }}</span>
          <span v-else class="info-color">未开卡</span>
          <v-icon name="arrow-right"/>
          <span v-if="details.expire_time" class="primary-color user-select">{{ details.expire_time | timeToYmdHi }}</span>
          <span v-else class="info-color">未开卡</span>
        </template>
        <template v-else>
          <template v-if="details.expire_time">{{ details.expire_time | timeToYmdHi }}</template>
          <span v-else class="info-color">未开卡</span>
        </template>
      </el-form-item>

      <div class="field-group-label">支付信息</div>

      <el-form-item label="收款方式">
        <template v-if="details.old_card_payment_mode_name !== details.card_payment_mode_name">
          <span class="user-select">{{ details.old_card_payment_mode_name }}</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.card_payment_mode_name }}</span>
        </template>
        <template v-else>{{ details.card_payment_mode_name }}</template>
      </el-form-item>

      <el-form-item v-if="details.old_receipt || details.receipt" label="收款凭证">
        <template v-if="details.old_receipt !== details.receipt">
          <div class="diff-flex">
            <v-img v-if="details.old_receipt" class="table-cover-phone" :value="details.old_receipt"/>
            <span v-else class="info-color">无</span>
            <v-icon name="arrow-right" class="diff-arrow primary-color"/>
            <v-img v-if="details.receipt" class="table-cover-phone" :value="details.receipt"/>
            <span v-else class="info-color">无</span>
          </div>
        </template>
        <template v-else>
          <v-img v-if="details.receipt" class="table-cover-phone" :value="details.receipt"/>
        </template>
      </el-form-item>

      <el-form-item v-if="details.old_you_zan_number || details.you_zan_number" label="有赞券码">
        <template v-if="details.old_you_zan_number !== details.you_zan_number">
          <span class="user-select">{{ details.old_you_zan_number }}</span>
          <v-icon name="arrow-right"/>
          <span class="primary-color user-select">{{ details.you_zan_number }}</span>
        </template>
        <template v-else>{{ details.you_zan_number }}</template>
      </el-form-item>

      <el-form-item label="实际收款">
        <template v-if="details.old_actual_payment !== details.actual_payment">
          <span class="user-select">{{ details.old_actual_payment / 100 }}</span>
          <v-icon name="arrow-right"/>
          <span class="income-color user-select">{{ details.actual_payment | toYuan }}</span>
        </template>
        <template v-else><span class="income-color">{{ details.actual_payment | toYuan }}</span></template>
      </el-form-item>

      <el-form-item v-if="details.old_payment_remark || details.payment_remark" label="付款备注">
        <template v-if="details.old_payment_remark !== details.payment_remark">
          <span class="user-select">{{ details.old_payment_remark }}</span>
          <v-icon name="arrow-right"/>
          <span class="warning-color user-select">{{ details.payment_remark }}</span>
        </template>
        <template v-else><span class="warning-color">{{ details.payment_remark }}</span></template>
      </el-form-item>

      <el-form-item label="备注文件">
        <template v-if="(details.old_remark_files || []).length || (details.remark_files || []).length">
          <div v-if="JSON.stringify(details.old_remark_files || []) !== JSON.stringify(details.remark_files || [])" class="remark-diff">
            <template v-if="(details.old_remark_files || []).length">
              <v-remark-file-list :files="details.old_remark_files || []"/>
            </template>
            <span v-else class="info-color">未上传</span>
            <v-icon name="arrow-right" class="remark-diff-arrow primary-color"/>
            <template v-if="(details.remark_files || []).length">
              <v-remark-file-list :files="details.remark_files || []"/>
            </template>
            <span v-else class="info-color">未上传</span>
          </div>
          <v-remark-file-list v-else :files="details.remark_files || []"/>
        </template>
        <span v-else class="info-color">未上传</span>
      </el-form-item>

      <el-form-item label="所补差价">
        <span v-if="details.update_payment > 0" class="income-color">{{ details.update_payment | toYuan }}</span>
        <span v-else-if="details.update_payment < 0" class="pay-color">{{ details.update_payment | toYuan }}</span>
        <span v-else class="info-color">未收款</span>
      </el-form-item>

      <el-form-item label="补差价支付方式">
        <span v-if="details.update_payment_mode" class="primary-color">{{ findPaymentModeName(details.update_payment_mode) }}</span>
        <span v-else class="info-color">未收款</span>
      </el-form-item>
      </template>

      <!-- 课次券提示 -->
      <v-card-ticket-warning
        v-if="cardTickets.length && details.old_student.id !== details.student.id"
        :tickets="cardTickets"
        message="该课卡已绑定以下课次券，审核通过后这些课次券将自动失效，请确认。"
      />

      <div class="table-title">审核信息</div>

      <el-form-item label="审核状态">
        <template v-if="!isLoading && !isLoadingError">
          <span
            v-for="applyState in findApplyState(details.update_state)"
            :key="`ApplyStateTag${applyState.value}`"
            :class="`${applyState.tagType}-color`">
            {{ applyState.name }}
          </span>
        </template>
      </el-form-item>

      <template v-if="details.update_state === applyStates.passed.value || details.update_state === applyStates.rejected.value">
        <el-form-item v-if="canViewReviewInfo" label="审核者">
          <template v-if="details.reviewer && details.reviewer.id">
            <v-avatar-nickname :value="details.reviewer.avatar" :nickname="details.reviewer.nickname" :is-center="false"/>
          </template>
          <span v-else class="info-color">未记录</span>
        </el-form-item>

        <el-form-item v-if="canViewReviewInfo" :label="`${details.update_state === applyStates.revoked.value ? '撤回' : '审核'}时间`">
          <template v-if="details.update_time">{{ details.update_time | timeToYmdHi }}</template>
          <span v-else class="info-color">未记录</span>
        </el-form-item>

        <el-form-item v-if="details.update_state === applyStates.rejected.value && (canViewReviewInfo)" label="驳回原因">
          <span v-if="details.review_remark" class="danger-color">{{ details.review_remark }}</span>
          <span v-else class="info-color">未填写</span>
        </el-form-item>

        <el-form-item v-if="details.update_state === applyStates.passed.value && isAdministrator" label="审核备注">
          <span v-if="details.review_remark" class="danger-color">{{ details.review_remark }}</span>
          <span v-else class="info-color">未填写</span>
        </el-form-item>
      </template>

      <template
        v-if="!isLoading && !isLoadingError && details.update_state === applyStates.review.value"
      >
        <template v-if="isAdministrator">
          <el-form-item label="审核结果">
            <el-radio class="pass-radio" v-model="isReject" :label="0" :disabled="isExamining">通过</el-radio>
            <el-radio class="reject-radio" v-model="isReject" :label="1" :disabled="isExamining">驳回</el-radio>
          </el-form-item>

          <el-form-item :label="isReject ? '驳回原因' : '审核备注'" prop="review_remark" :required="!!isReject">
            <el-input
              type="textarea"
              :autosize="{ minRows: 4 }"
              :placeholder="`请输入 2 ~ 200 字的${isReject ? '驳回原因' : '审核备注'}`"
              v-model="details.review_remark"
              :maxlength="200"
              show-word-limit
              clearable
              :disabled="isExamining"
            />
          </el-form-item>

          <el-form-item>
            <el-button class="examine-btn" :class="{ 'pass-btn': !isReject }" :type="isReject ? 'danger' : 'success'" @click="handleExamine" :loading="isExamining">{{ isReject ? '立即驳回' : '立即通过' }}</el-button>
            <el-button @click="RouteConfig.cardUpdateApplyList.back">返回</el-button>
          </el-form-item>
        </template>

        <el-form-item v-else-if="canRevoke">
          <el-button type="danger" @click="handleRevoke" :loading="isRevoking">撤销</el-button>
          <el-button @click="RouteConfig.cardUpdateApplyList.back">返回</el-button>
        </el-form-item>
        <el-form-item v-else>
          <el-button @click="RouteConfig.cardUpdateApplyList.back">返回</el-button>
        </el-form-item>
      </template>
      <el-form-item v-else>
        <el-button @click="RouteConfig.cardUpdateApplyList.back">返回</el-button>
      </el-form-item>

    </el-form>
  </div>
</template>

<script>
import RouteConfig from '@/router/config';
import { mapGetters } from 'vuex';
import { applyStates, findApplyState } from '@/enums/applyStates';
import cardUpdateTypes from '@/enums/cardUpdateTypes';
import { destroy, show, update } from '@/api/card-update-apply';
import { store } from '@/api/card-update-record';
import { findPaymentModeName } from '@/enums/paymentModes';
import { toCent, toYuan } from '@/filters';

export default {
  name: RouteConfig.cardUpdateApplyDetail.name,
  data: () => ({
    RouteConfig,
    isLoading: false,
    isLoadingError: false,
    isExamining: false,
    isRevoking: false,
    isReject: 0,
    applyStates,
    cardTickets: [],
    // 审核停卡天数输入框（单位：天，支持两位小数）；接口下发的是「天×100」，通过 toCent / toYuan 换算
    stopDaysInput: 30,
    details: {
      add_time: 0,
      update_time: 0,
      add_store_name: '',
      store_name: '',
      store_names: '',
      old_store_names: '',
      administrator: {},
      reviewer: {},
      student: {},
      old_student: {},
      update_state: applyStates.review.value,
      update_type: 0,
      update_remark: '',
      review_remark: '',
      store_id: 0,
      card_id: 0,
      stop_reason_type_name: '',
      stop_days: 0,
      stop_days_approved: 0,

      card_name: '',
      card_type_name: '',
      is_number_limit: 0,
      expire_number: 0,
      expire_days: 0,
      expire_time: 0,
      store_limit_expire_days: 0,
      store_limit_expire_time: 0,
      you_zan_number: '',
      actual_payment: 0,
      card_payment_mode_id: 0,
      card_payment_mode_name: '',
      payment_remark: '',
      receipt: '',
      remark_files: [],
      register_time: 0,
      start_time_auto: 0,
      is_disable_column: 0,
      update_payment: 0,
      update_payment_mode: 0,

      old_card_name: '',
      old_card_type_name: '',
      old_is_number_limit: 0,
      old_expire_number: 0,
      old_expire_days: 0,
      old_expire_time: 0,
      old_store_limit_expire_days: 0,
      old_store_limit_expire_time: 0,
      old_you_zan_number: '',
      old_actual_payment: 0,
      old_card_payment_mode_id: 0,
      old_card_payment_mode_name: '',
      old_payment_remark: '',
      old_receipt: '',
      old_remark_files: [],
      old_register_time: 0,
      old_start_time_auto: 0,
      old_is_disable_column: 0,
    },
    rules: {
      review_remark: [],
    },
  }),

  computed: {
    ...mapGetters(['isAdministrator', 'userStoreId']),
    canViewReviewInfo() {
      return this.isAdministrator || this.userStoreId === this.details.store_id;
    },
    isStopApply() {
      return this.details.update_type === cardUpdateTypes.stop.value;
    },
    // 撤销入口：超管无撤回权（用驳回）；学员自助停卡申请门店只能查看；仅本店可操作
    canRevoke() {
      if (this.isAdministrator) return false;
      if (this.details.update_state !== applyStates.review.value) return false;
      if (this.isStopApply) return false;
      return this.userStoreId === this.details.store_id;
    },
    // 仅停卡申请、且当前处于审核中、且操作者是超级管理员时，才允许调整停卡天数
    canEditStopDays() {
      return this.isStopApply
        && this.isAdministrator
        && this.details.update_state === applyStates.review.value;
    },
    // 输入组件对「上限」实时钳制、对「下限」失焦钳制；这里只对「低于下限」给红字提示，空值交给提交校验兜底
    isStopDaysInputInvalid() {
      if (this.stopDaysInput === '' || this.stopDaysInput === null || this.stopDaysInput === undefined) return false;
      const days = Number(this.stopDaysInput);
      return !(Number.isFinite(days) && days >= 0.5 && days <= 365);
    },
    id() {
      return +this.$route.params.id || 0;
    },
  },

  watch: {
    isReject() {
      if (this.$refs.form) {
        this.$refs.form.clearValidate('review_remark');
      }
    },
  },

  created() {
    this.rules.review_remark = [{
      validator: (rule, value, callback) => {
        if (this.isReject && (!value || !value.trim())) {
          callback(new Error('请输入 2 ~ 200 字的驳回原因'));
        } else {
          callback();
        }
      },
    }];
  },

  beforeMount() {
    this.show();
  },

  methods: {
    findApplyState,
    findPaymentModeName,
    findCardUpdateTypeName(value) {
      for (const key in cardUpdateTypes) {
        if (value === cardUpdateTypes[key].value) return cardUpdateTypes[key].name;
      }
      return '';
    },
    async show() {
      if (this.isLoading) return;

      this.isLoading = true;
      this.isLoadingError = false;
      const { isError, data } = await show(this.id);
      if (isError) {
        this.isLoading = false;
        this.isLoadingError = true;
        return;
      }

      data.old_card_payment_mode_name = findPaymentModeName(data.old_card_payment_mode_id);
      data.card_payment_mode_name = findPaymentModeName(data.card_payment_mode_id);
      this.cardTickets = data.card_tickets || [];

      const { details } = this;
      const updateData = {};
      Object.keys(data).forEach(key => {
        if (key in details) updateData[key] = data[key];
      });
      Object.assign(details, updateData);
      // 停卡申请：审核天数输入框（单位「天」，支持两位小数）默认取学员申请值；
      // 接口下发的是「天×100」，用 toYuan 折算回天
      if (details.update_type === cardUpdateTypes.stop.value) {
        this.stopDaysInput = toYuan(details.stop_days_approved || details.stop_days);
      }
      this.isLoading = false;
    },

    handleExamine() {
      if (this.isExamining) return;
      this.$refs.form.validate(async (isSuccess) => {
        if (!isSuccess) return;
        this.isExamining = true;
        this.isReject ? this.handleReject() : this.handlePass();
      });
    },

    async handlePass() {
      const payload = {
        card_update_apply_id: this.id,
        review_remark: this.details.review_remark,
      };
      // 停卡申请：把审核确认天数（超管可改）一并提交，未改则为学员申请值。
      // 这里做一次硬校验，兜住“输入超范围后未失焦、未经钳制就直接提交”的情况。
      if (this.isStopApply) {
        const days = Number(this.stopDaysInput);
        if (!(Number.isFinite(days) && days >= 0.5 && days <= 365)) {
          this.isExamining = false;
          return this.$message.warning('停卡天数需在 0.5 ~ 365 天之间。');
        }
        payload.stop_days = toCent(days);
      }

      const { isError } = await store(payload);
      this.isExamining = false;
      if (!isError) RouteConfig.cardUpdateApplyList.push();
    },

    async handleReject() {
      const { isError } = await update(this.id, {
        review_remark: this.details.review_remark,
      });
      this.isExamining = false;
      if (!isError) RouteConfig.cardUpdateApplyList.push();
    },

    async handleRevoke() {
      if (this.isRevoking) return;
      this.isRevoking = true;
      const { isError } = await destroy(this.id);
      this.isRevoking = false;
      if (!isError) RouteConfig.cardUpdateApplyList.push({ apply_states: [applyStates.revoked.value] });
    },
  },
};
</script>
<style src="./card-detail.css" scoped/>
<style scoped>
.remark-diff,
.diff-flex {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.remark-diff-arrow,
.diff-arrow {
  color: #409EFF !important;
  flex-shrink: 0;
}
</style>
<style src="./update-apply-detail.css" scoped/>
